import { REQUIRED_FIELD_MESSAGE } from "@src/constants/constants";
import { z } from "zod";
import SignolUtils from "@src/utils/signolUtils";

export const loginEmailSchema = z.object({
  username: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export const loginUsernameSchema = z.object({
  username: z.string().min(2, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export const signupEmailSchema = z.object({
  username: z.string().email("Invalid email address"),
  firstname: z.string().optional(),
  lastname: z.string().min(2, "Length must be at least 2 characters"),
});
export const signupUsernameSchema = z.object({
  username: z.string().min(2, "Username is required"),
  firstname: z.string().optional(),
  lastname: z.string().min(2, "Length must be at least 2 characters"),
});

/**
 * Field that is cleaned before it is validated, so validation runs against the
 * value we actually submit. `normalize` returns "" for anything unusable, which
 * is what surfaces `invalidMessage`.
 */
const cleanedField = (normalize, requiredMessage, invalidMessage) =>
  z
    .string({
      required_error: requiredMessage,
      invalid_type_error: requiredMessage,
    })
    .min(1, requiredMessage)
    .transform(normalize)
    .refine((value) => value.length > 0, {
      message: invalidMessage ?? requiredMessage,
    });

/**
 * Crew update fields are cleaned the same way the crew_rotations_job pipeline
 * cleans the crew rotation spreadsheets — see `SignolUtils.sanitizeCrewValue`.
 * A code entered as "XYZ-12345" must reach us as the sheet's "XYZ12345" would,
 * otherwise the same crew member exists twice under two spellings.
 */
export const buildCrewUpdateSchema = (includeVesselImo, codeLabel) => {
  const operatorCodeLabel = codeLabel || "Employee ID";

  return z.object({
    firstname: cleanedField(
      SignolUtils.normalizeCrewName,
      "First name is required",
    ),
    lastname: cleanedField(
      SignolUtils.normalizeCrewName,
      "Last name is required",
    ),
    role: z.enum(["MASTER", "CHIEF_ENGINEER"], {
      required_error: "Role is required",
    }),
    employeeId: cleanedField(
      SignolUtils.normalizeCrewOperatorCode,
      `${operatorCodeLabel} is required`,
      `${operatorCodeLabel} must contain letters or numbers`,
    ),
    ...(includeVesselImo && {
      vesselImo: cleanedField(
        SignolUtils.sanitizeCrewValue,
        "Vessel IMO is required",
      ),
    }),
    joinedVesselOn: cleanedField(
      SignolUtils.normalizeCrewDate,
      "Date is required",
      "Enter a valid date",
    ),
  });
};

export const exampleSchema = z.object({
  username: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export const notifSchema = z.object({
  opt_in_accepted_level_1: z.boolean().default(true),
  opt_in_accepted_level_2: z.boolean().default(true),
  opt_in_accepted_level_3: z.boolean().default(true),
  opt_in_accepted_level_4: z.boolean().default(true),
});

export const cognitoPasswordValidator = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z-À-Ü]/, "Must contain at least one uppercase letter")
  .regex(/[a-z-à-ü]/, "Must contain at least one lowercase letter")
  .regex(/[0-9]/, "Must contain at least one number")
  .regex(
    /[@$!#%*?~+=&_\-\.\[\]\{\}\(\)]/,
    "Must contain one special character",
    // aws cognito allows: '@ $ ! # % * ? ~ + = & _ - . [ ] { } ( )'
  );

export const changePasswordSchema = z
  .object({
    passwordOld: z.string().min(1, REQUIRED_FIELD_MESSAGE),
    passwordNew: cognitoPasswordValidator,
    passwordNewConfirm: z.string().min(1, REQUIRED_FIELD_MESSAGE),
  })
  .refine((data) => data.passwordNew !== data.passwordOld, {
    path: ["passwordNew"],
    message: "Cannot be the same as the old password",
  })
  .refine((data) => data.passwordNew === data.passwordNewConfirm, {
    path: ["passwordNewConfirm"],
    message: "Please ensure both passwords match.",
  });

export const forgotPasswordEmailSchema = z.object({
  username: z.string().email("Please enter a valid email address"),
});

export const forgotPasswordUsernameSchema = z.object({
  username: z.string().min(3, "Please enter a valid username"),
});

export const resetPasswordSchema = z
  .object({
    password: cognitoPasswordValidator,
    password2: z.string().min(1, REQUIRED_FIELD_MESSAGE),
  })
  .refine((data) => data.password === data.password2, {
    path: ["password2"],
    message: "Please ensure both passwords match.",
  });

export const securityQuestionSchema = z.object({
  answerOptions: z
    .array(z.boolean())
    .refine((answers) => answers.some((answer) => answer === true), {
      message: "Please select an answer",
    }),
});

export const adminCustomerSchema = z.object({
  customerCode: z.string().default(""),
  name: z.string().default(""),
  language: z.string().default("en"),
  industry: z.string(),
  targetPeriodDuration: z.string().optional().default(""),
  baselinePeriod: z.union([z.string(), z.number()]),
  reportingDelay: z.union([z.string(), z.number()]).default(0),
  preferredEventName: z.string().optional().default(""),
  preferredEventNamePlural: z.string().optional().default(""),
  contractStartDate: z.string().optional().default(""),
  contractEndDate: z.string().optional().default(""),
  operatorCodeLabel: z.string().optional().default(""),
  corporateEmails: z.string().optional().default(""),
  enrollmentType: z.string().optional().default(""),
});

export const charitySchema = z.object({
  name: z.string().default(""),
  description: z.string().optional().default(""),
  donationStatement: z.string().optional().default(""),
  donationAmount: z.union([z.string(), z.number()]),
  videoId: z.string().optional().default(""),
  url: z.string().optional().default(""),
});

export const teamSchema = z.object({
  name: z.string().default(""),
  alias: z.string().optional().default(""),
  isDefault: z.boolean().default(false),
  eventTypeId: z.string().default(""),
  baselineSuccessRateStartDate: z.string().optional().default(""),
  baselineSuccessRateEndDate: z.string().optional().default(""),
  baselineEventCountEndDate: z.string().optional().default(""),
});

export const behaviourSchema = z.object({
  acronym: z.string().default(""),
  behaviourType: z.string().default(""),
  behaviourTypeId: z.string().default(""),
  name: z.string().default(""),
  enabled: z.boolean().default(false),
  description: z.string().optional().default(""),
  terminology: z.string().optional().default(""),
  reasons: z.string().optional().default(""),
  fuelSavingsSource: z.string().optional().default(""),
  position: z.union([z.string(), z.number()]).optional().default(0),
});

export const targetSchema = z.object({
  name: z.string().default(""),
  behaviourTypeId: z.string().default(""),
  targetPercentage: z.union([z.string(), z.number()]).optional().default(null),
  startDate: z.string().nullish().optional().default(null),
  baselineSuccessRateStartDate: z.string().nullish().optional().default(null),
  baselineSuccessRateEndDate: z.string().nullish().optional().default(null),
  baselineEventCountEndDate: z.string().nullish().optional().default(null),
});
